import { useDraggable, useDroppable } from "@dnd-kit/core";
import { Box } from "@mui/material";
import { formatDate, formatDistanceToNow } from "date-fns";
import { MouseEvent } from "react";

import { useTheme } from "../../components/ui-v2";
import { useDrive } from "./DriveContext";
import { DriveGridItem } from "./DriveGridItem";
import DriveItem, { EllipsisButton } from "./DriveItem";
import { DriveDirectoryFragment } from "./queries.graphql";
import { DriveViewType } from "./types";
import { tableViewColumns } from "./utils";

type DriveDirectoryViewProps = {
  viewType: DriveViewType;
  directory: DriveDirectoryFragment;
  isSelected: boolean;
  isSelectable: boolean;
  onClick: (event: MouseEvent) => void;
  onContextMenu: (event: MouseEvent) => void;
  onDoubleClick: (event: MouseEvent) => void;
};

export default function DriveDirectoryView({
  viewType,
  directory,
  isSelected,
  isSelectable,
  onClick,
  onContextMenu,
  onDoubleClick,
}: DriveDirectoryViewProps) {
  const theme = useTheme();
  const { actionsDisabled } = useDrive();

  // droppable
  const { isOver, setNodeRef: droppableNodeRef } = useDroppable({
    id: `directory__${directory.id}`,
    data: {
      type: "directory",
      item: directory,
    },
  });
  // draggable
  const {
    attributes,
    listeners,
    setNodeRef: draggableNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: `directory__${directory.id}`,
    data: {
      supports: ["directory"],
      item: directory,
    },
  });
  const draggableStyle = transform
    ? {
        ...(isDragging && {
          pointerEvents: "none" as const, // this is necessary to prevent the link from being clicked while dragging
        }),
      }
    : undefined;

  const renderInner = () => {
    switch (viewType) {
      case "table":
        return (
          <>
            {tableViewColumns.map((column, index: number) => {
              return (
                <Box
                  key={column.id}
                  sx={{
                    width: column.width,
                    flexGrow: column.width ? 0 : 1,
                    flexShrink: column.width ? 0 : 1,
                    overflow: "hidden",
                    display: "-webkit-box",
                    "-webkit-box-orient": "vertical",
                    "-webkit-line-clamp": "1",
                    paddingRight: 4,
                    paddingLeft: 4,

                    ...(index > 1 && {
                      fontWeight: 300,
                      color: theme.palette.secondary[2],
                    }),
                  }}
                >
                  {column.directoryValue?.(directory) || ""}
                </Box>
              );
            })}
            {!actionsDisabled && (
              <EllipsisButton onClick={onContextMenu} isSelected={isSelected} />
            )}
          </>
        );
      case "grid":
        return (
          <DriveGridItem
            fileType="directory"
            text={directory.name}
            subText={formatDistanceToNow(directory.createdAt, {
              addSuffix: true,
            })}
            isSelected={isSelected}
            isDragging={isDragging}
            onContextMenu={onContextMenu}
            onFileClick={onClick}
            onFileDoubleClick={onDoubleClick}
          />
        );
    }
  };

  return (
    <DriveItem
      title={`${directory.name || "Untitled"} (created at ${formatDate(directory.createdAt, "PPpp")})`}
      type="Directory"
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onContextMenu={onContextMenu}
      draggableStyle={draggableStyle}
      isSelected={isSelected}
      isDragging={isDragging}
      isSelectable={isSelectable}
      isOver={isOver}
      viewType={viewType}
      ref={(el) => {
        if (el instanceof HTMLElement) {
          draggableNodeRef(el);
          droppableNodeRef(el);
        }
      }}
      {...listeners}
      {...attributes}
    >
      {renderInner()}
    </DriveItem>
  );
}
